// Row-level review for a PRATUS AI asset import (create_org_assets). The
// reviewer's choices are sent back as inline edits.
import React, { useMemo } from 'react';
import {
  currentSelection,
  currentUpdateExisting,
  selectionEdits,
} from '../assetImportReviewModel';
import type { ItemState, PendingConfirmation } from '../types';
import { AssetImportRowsTable } from './AssetImportRowsTable';

/** Summary fields from the preview worth showing above the table. */
const SUMMARY_FIELDS: { field: string; label: string }[] = [
  { field: 'source', label: 'Source' },
  { field: 'threats', label: 'Threats' },
  { field: 'automation_rules', label: 'Automation rules' },
];

interface Props {
  item: PendingConfirmation;
  state?: ItemState;
  onEdit: (field: string, value: unknown) => void;
}

const AssetImportReview: React.FC<Props> = ({ item, state, onEdit }) => {
  const rows = useMemo(() => item.preview?.rows ?? [], [item]);

  const selected = currentSelection(rows, state?.edits);
  const updateExisting = currentUpdateExisting(item.args, state?.edits);

  const setSelected = (next: number[]) => {
    const edits = selectionEdits(rows, next);
    onEdit('include_rows', edits.include_rows);
    onEdit('exclude_rows', edits.exclude_rows);
  };

  const summary = SUMMARY_FIELDS.map(({ field, label }) => ({
    label,
    value: item.preview?.diff.find((d) => d.field === field)?.after,
  })).filter(({ value }) => value !== undefined && value !== '');

  return (
    <div data-testid="asset-import-review">
      {summary.length > 0 && (
        <dl className="tw:mb-3 tw:grid tw:grid-cols-[auto_1fr] tw:gap-x-3 tw:gap-y-1 tw:text-[13px]">
          {summary.map(({ label, value }) => (
            <React.Fragment key={label}>
              <dt className="tw:text-muted-foreground">{label}</dt>
              <dd className="tw:m-0 tw:break-words tw:text-foreground">
                {String(value)}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      <AssetImportRowsTable
        rows={rows}
        selected={selected}
        updateExisting={updateExisting}
        onSelectedChange={setSelected}
        onUpdateExistingChange={(checked) => onEdit('update_existing', checked)}
      />
    </div>
  );
};

export default AssetImportReview;
